// Core lib dependencies.
import React from 'react';
import PropTypes from 'prop-types';

/**
 * <pre>
 * Button React components.
 *
 * Summary of responsibilities:
 *
 *  - set of components and helper functions to handle rendering and functionality for buttons.
 * </pre>
 *
 * @author Various
 * @namespace Common.Components.Buttons
 */

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
/* Components */

/**
 * Standard (text only) button.
 *
 * @namespace
 * @memberOf Common.Components.Buttons
 */
class Button extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);

    // Bind methods.
    this.handleClick = this.handleClick.bind(this);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {ReactElement}
   */
  render() {
    var classNames = getButtonClasses(this.props.className, this.props.addClasses);

    //  only calling handleClick of 'onClick' is provided in props
    const onClick = this.props.onClick && this.handleClick;

    return (
      <a
        onClick={onClick}
        target={this.props.target}
        href={this.props.href}
        className={classNames.join(' ')}
        role={this.props.role}
        aria-label={this.props.ariaLabel}
        title={this.props.title}
        {...this.props.dataAttributes}
      >
        <span>{this.props.children}</span>
      </a>
    );
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Handler methods */

  /**
   * Click handler
   *
   */
  handleClick(event) {
    // Stop the default click behavior (eg. will otherwise scroll the page to the top).
    event.preventDefault();
    event.stopPropagation();

    // If an injected handler prop exists.
    if (this.props.onClick) {
      // Call the injected handler prop.
      this.props.onClick(event);
    }
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {String} className String of css classnames to append to the anchor element.
 * @property {String} href The href attribute.
 * @property {String} target The target window.
 * @property {Function} onClick Click event handler function.
 * @property {Object} dataAttributes Object containing k/v pairs of data attributes to append to the anchor element.
 * @property {String} role The role attribute.
 * @property {String} ariaLabel The aria-label attributes.
 * @property {String} title provides a very basic browser-made tooltip.
 */
Button.propTypes = {
  className:      PropTypes.string,
  href:           PropTypes.string,
  target:         PropTypes.string,
  onClick:        PropTypes.func,
  dataAttributes: PropTypes.object,
  role:           PropTypes.string,
  ariaLabel:      PropTypes.string,
  title:          PropTypes.string,
};

/**
 * React default props.
 *
 * @type {Object}
 * @property {String} className xButton
 */
Button.defaultProps = {
  className: 'xButton',
  href:      '#',
  role:      'button',
};

export { Button };

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * Hybrid (text/child elements with image icon) button.
 *
 * @namespace
 * @memberOf Common.Components.Buttons
 */
class HybridButton extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);

    // Bind methods.
    this.handleClick = this.handleClick.bind(this);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {ReactElement}
   */
  render() {
    var _icon = <i className={this.props.iconClass} />;

    return (
      <a
        className={this.props.className}
        onClick={this.handleClick}
        href={this.props.href}
        role={this.props.role}
        aria-label={this.props.ariaLabel}
        {...this.props.dataAttributes}
      >
        <span>
          {_icon}
          {this.props.children}
        </span>
      </a>
    );
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Handler methods */

  /**
   * Click handler
   *
   */
  handleClick(event) {
    // Stop the default click behavior (eg. will otherwise scroll the page to the top).
    event.preventDefault();
    event.stopPropagation();

    // If an injected handler prop exists.
    if (this.props.onClick) {
      // Call the injected handler prop.
      this.props.onClick(event);
    }
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {String} iconClass String of css classnames to append to the icon element.
 * @property {String} className String of css classnames to append to the anchor element.
 * @property {String} href The href attribute.
 * @property {Function} onClick Click event handler function.
 * @property {Object} dataAttributes Object containing k/v pairs of data attributes to append to the anchor element.
 * @property {String} role The role attribute.
 * @property {String} ariaLabel The aria-label attributes.
 */
HybridButton.propTypes = {
  iconClass:      PropTypes.string,
  className:      PropTypes.string,
  href:           PropTypes.string,
  onClick:        PropTypes.func,
  dataAttributes: PropTypes.object,
  role:           PropTypes.string,
  ariaLabel:      PropTypes.string,
};

/**
 * React default props.
 *
 * @type {Object}
 * @property {String} iconClass xIcon
 */
HybridButton.defaultProps = {
  iconClass: 'xIcon',
  href:      '#',
  role:      'button',
};

export { HybridButton };

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * Paging button.
 *
 * @namespace
 * @memberOf Common.Components.Buttons
 */
class PagingButton extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);

    // Bind methods.
    this.handleClick = this.handleClick.bind(this);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {ReactElement}
   */
  render() {
    var classNames = getButtonClasses(this.props.className, this.props.addClasses);

    return (
      <a onClick={this.handleClick} href={this.props.href} className={classNames.join(' ')} role={this.props.role}>
        <span>{this.props.children}</span>
      </a>
    );
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Handler methods */

  /**
   * Click handler
   *
   */
  handleClick(event) {
    // Stop the default click behavior (eg. will otherwise scroll the page to the top).
    event.preventDefault();
    event.stopPropagation();

    // If an injected handler prop exists.
    if (this.props.onClick) {
      // Call the injected handler prop.
      this.props.onClick(event);
    }
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {String} className String of css classnames to append to the anchor element.
 * @property {String} href The href attribute.
 * @property {String} addClasses String of additional css classnames to append to the anchor element.
 * @property {Function} onClick Click event handler function.
 * @property {String} role The role attribute.
 */
PagingButton.propTypes = {
  className:  PropTypes.string,
  href:       PropTypes.string,
  addClasses: PropTypes.string,
  onClick:    PropTypes.func,
  role:       PropTypes.string,
};

/**
 * React default props.
 *
 * @type {Object}
 * @property {String} className xPagingButton xActionPaginate
 */
PagingButton.defaultProps = {
  className: 'xPagingButton xActionPaginate',
  href:      '#',
  role:      'button',
};

export { PagingButton };

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
/* Private utility functions */

/**
 * Returns css classes for button.
 *
 * @private
 */
function getButtonClasses(className, addClasses, disabled = false) {
  var _classes = [];

  if ('string' === typeof className) {
    _classes.push(className);
  } // TODO: If array, concat and push

  if ('string' === typeof addClasses) {
    _classes.push(addClasses);
  } // TODO: If array, concat and push

  if (disabled) _classes.push('xDisabled');

  return _classes;
}
